import { SdkContext } from '../internal'
import { fetchFile, type DownloadedFile } from '../fileDownload'
import type {
  CompanyValue,
  Recognition,
  RecognitionComment,
  RecognitionDashboard,
  RecognitionInput,
  RecognitionPage,
  RecognitionPerson,
  RecognitionType,
  RecognitionTypeInfo
} from '@/types/recognition'

const toPage = (result: unknown): RecognitionPage => {
  if (Array.isArray(result)) return { items: result as Recognition[], pagination: null }
  const r = result as { items?: Recognition[]; data?: Recognition[]; pagination?: RecognitionPage['pagination'] } | null
  return { items: r?.items || r?.data || [], pagination: r?.pagination ?? null }
}

const arrayOf = <T,>(result: unknown): T[] => {
  if (Array.isArray(result)) return result as T[]
  const r = result as { items?: T[]; data?: T[] } | null
  return r?.items || r?.data || []
}

/**
 * F-53 Recognition. The community comes from X-CommunityId (never the path);
 * a community may have several Recognition spaces, so the routes that act in
 * one take `spaceId` (422 `spaceRequired` without it when there are several).
 * Company values are community-wide. The server decides who sees, deletes and
 * manages. Paths written by hand (F-03).
 */
export class RecognitionsResource {
  constructor(private ctx: SdkContext) {}

  /** The wall, newest first. `valueId`: one company value, or `none` (without a value). */
  async list(params: { page?: number; limit?: number; memberId?: string; spaceId?: string; valueId?: string; type?: RecognitionType } = {}): Promise<RecognitionPage> {
    return toPage(await this.ctx.raw({ httpMethod: 'GET', path: '/recognitions' }, { query: params }))
  }

  async get(id: string): Promise<Recognition> {
    return this.ctx.call({ httpMethod: 'GET', path: '/recognitions/{id}' }, { id })
  }

  /** The response carries `emailNotice` (the toast). */
  async create(body: RecognitionInput): Promise<Recognition> {
    return this.ctx.call({ httpMethod: 'POST', path: '/recognitions', hasBody: true }, { body })
  }

  async remove(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/recognitions/{id}' }, { id })
  }

  /** One reaction per person; returns the card again. */
  async react(id: string, reaction: string): Promise<Recognition> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/recognitions/{id}/reaction', hasBody: true }, { id, body: { reaction } })
  }

  async unreact(id: string): Promise<Recognition> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/recognitions/{id}/reaction' }, { id })
  }

  /** Oldest first, replies nested one level. */
  async listComments(id: string): Promise<RecognitionComment[]> {
    return arrayOf<RecognitionComment>(await this.ctx.call({ httpMethod: 'GET', path: '/recognitions/{id}/comments' }, { id }))
  }

  async addComment(id: string, body: { content: string; parentId?: string | null }): Promise<RecognitionComment> {
    return this.ctx.call({ httpMethod: 'POST', path: '/recognitions/{id}/comments', hasBody: true }, { id, body })
  }

  async removeComment(id: string, commentId: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/recognitions/{id}/comments/{commentId}' }, { id, commentId })
  }

  /** Who can be recognized: people who open the space, never the caller. */
  async people(search?: string, spaceId?: string): Promise<RecognitionPerson[]> {
    return arrayOf<RecognitionPerson>(
      await this.ctx.call({ httpMethod: 'GET', path: '/recognitions/people' }, { query: { search: search?.trim() || undefined, spaceId } })
    )
  }

  /** Active values (the same list in every Recognition space); owner/admin also get the inactive ones with `includeInactive`. */
  async listValues(includeInactive = false, spaceId?: string): Promise<CompanyValue[]> {
    return arrayOf<CompanyValue>(
      await this.ctx.call({ httpMethod: 'GET', path: '/recognitions/values' }, { query: { ...(includeInactive ? { includeInactive: true } : {}), spaceId } })
    )
  }

  async createValue(body: { name: string; description?: string | null }): Promise<CompanyValue> {
    return this.ctx.call({ httpMethod: 'POST', path: '/recognitions/values', hasBody: true }, { body })
  }

  async updateValue(valueId: string, body: { name?: string; description?: string | null; active?: boolean }): Promise<CompanyValue> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/recognitions/values/{valueId}', hasBody: true }, { valueId, body })
  }

  /** Every value id once, in the new order. */
  async reorderValues(ids: string[]): Promise<CompanyValue[]> {
    return arrayOf<CompanyValue>(
      await this.ctx.call({ httpMethod: 'PUT', path: '/recognitions/values/order', hasBody: true }, { body: { ids } })
    )
  }

  /** Owner/admin; days in the community's time zone. 403 for anyone else. */
  async dashboard(params: { from?: string; to?: string; spaceId?: string } = {}): Promise<RecognitionDashboard> {
    return this.ctx.call({ httpMethod: 'GET', path: '/recognitions/dashboard' }, { query: params })
  }

  /** F-185: the seven kinds with their art, enabled or not (anyone who opens a Recognition space). */
  async listTypes(spaceId?: string): Promise<RecognitionTypeInfo[]> {
    return arrayOf<RecognitionTypeInfo>(await this.ctx.call({ httpMethod: 'GET', path: '/recognitions/types' }, { query: { spaceId } }))
  }

  /** Owner/admin. Turning off the last enabled kind is a 400 (`lastTypeEnabled`). */
  async setType(type: RecognitionType, enabled: boolean): Promise<RecognitionTypeInfo> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/recognitions/types/{type}', hasBody: true }, { type, body: { enabled } })
  }

  /** Owner/admin. PNG, JPG or WebP up to 5 MB and at least 1000 px wide; the API stores it as 1200x630. */
  async uploadTypeArt(type: RecognitionType, file: File): Promise<RecognitionTypeInfo> {
    const fd = new FormData()
    fd.append('art', file)
    return this.ctx.call({ httpMethod: 'POST', path: '/recognitions/types/{type}/art', hasBody: true }, { type, body: fd })
  }

  /** Owner/admin: back to the default art (the custom file is deleted). */
  async removeTypeArt(type: RecognitionType): Promise<RecognitionTypeInfo> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/recognitions/types/{type}/art' }, { type })
  }

  /**
   * F-106 6: the share card (PNG, 1200 wide, 3:1 art). Only with the item's `canShare`; 403 `cardDenied`
   * for another reader, 404 for whoever cannot read it. `download: false` asks for it inline.
   */
  async card(id: string, download = true): Promise<DownloadedFile> {
    return fetchFile(this.ctx, `/recognitions/${encodeURIComponent(id)}/card`, { download }, 'image/png', `recognition-${id}.png`)
  }
}
